import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Session token store.
 *
 * The backend sets httpOnly cookies AND returns raw tokens in the login /
 * register / refresh response body. Cookies are the primary path — but
 * privacy-heavy browsers (Opera Android, Firefox Focus, Brave with
 * strict shields, Samsung Internet with anti-tracking) silently drop
 * cross-site cookies. For those users we cache the tokens in
 * localStorage and attach them as an `Authorization: Bearer` header on
 * every request. The server accepts either — whichever arrives.
 */
const AT_KEY = "atlas.access_token";
const RT_KEY = "atlas.refresh_token";

export const tokenStore = {
    getAccess: () => {
        try { return localStorage.getItem(AT_KEY) || null; } catch { return null; }
    },
    getRefresh: () => {
        try { return localStorage.getItem(RT_KEY) || null; } catch { return null; }
    },
    /** Persist tokens after login / register / refresh. Silently no-ops
        if the response didn't include them (older backend versions). */
    save: (data) => {
        try {
            if (data?.access_token) localStorage.setItem(AT_KEY, data.access_token);
            if (data?.refresh_token) localStorage.setItem(RT_KEY, data.refresh_token);
        } catch {}
    },
    clear: () => {
        try {
            localStorage.removeItem(AT_KEY);
            localStorage.removeItem(RT_KEY);
        } catch {}
    },
};

const BACKEND_URL_TRIMMED = BACKEND_URL;

const api = axios.create({
    baseURL: `${BACKEND_URL_TRIMMED}/api`,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// Attach Bearer header on EVERY request if we have a cached access token.
// Servers that already got the cookie ignore the header; servers that
// didn't get the cookie honour the header. Belt-and-braces.
api.interceptors.request.use((config) => {
    const at = tokenStore.getAccess();
    if (at && !config.headers?.Authorization) {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${at}` };
    }
    return config;
});

const NO_REFRESH_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];

let pendingRefresh = null;

function sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
}

async function tryRefresh() {
    if (!pendingRefresh) {
        // Include the cached refresh_token in the header AND body — the
        // server checks cookie first, then header, then body. This is the
        // whole point of the fallback: privacy browsers with no cookies
        // still succeed via the header path.
        const rt = tokenStore.getRefresh();
        const config = rt ? { headers: { "X-Refresh-Token": rt } } : {};
        const body = rt ? { refresh_token: rt } : {};
        pendingRefresh = api
            .post("/auth/refresh", body, config)
            .then((r) => {
                tokenStore.save(r.data);
                return r;
            })
            .finally(() => {
                pendingRefresh = null;
            });
    }
    return pendingRefresh;
}

function signalAuthExpired() {
    try {
        if (typeof window !== "undefined" && !window.__atlasAuthFlushed) {
            window.__atlasAuthFlushed = true;
            tokenStore.clear();
            window.dispatchEvent(new CustomEvent("atlas:auth-expired"));
        }
    } catch {}
}

// Paths that opt out of the global error toast. Anything auth-related is
// surfaced inline in the form, and fire-and-forget capture endpoints must
// never nag the user with a red toast if they fail silently in the background.
const SILENT_ERROR_PATHS = [
    "/auth/login",
    "/auth/register",
    "/auth/refresh",
    "/auth/logout",
    "/auth/me",
    "/leads/abandoned",
    "/emails/track",
    "/me/career-paths/cross-discipline", // hidden card, allowed to 404
];

let lastErrorToastAt = 0;
function surfaceGlobalError(error) {
    try {
        if (typeof window === "undefined") return;
        const url = error?.config?.url || "";
        if (SILENT_ERROR_PATHS.some((p) => url.includes(p))) return;
        const status = error?.response?.status;
        // Skip 4xx (client-handled) except pure network failure (no response)
        // and 5xx server errors. Also skip AbortError on route changes.
        if (error?.code === "ERR_CANCELED") return;
        const isNetwork = !error?.response;
        const is5xx = status >= 500 && status < 600;
        if (!isNetwork && !is5xx) return;
        // De-dupe: only one toast every 4s. Prevents a burst of failing
        // parallel calls from stacking up 5 identical red banners.
        const now = Date.now();
        if (now - lastErrorToastAt < 4000) return;
        lastErrorToastAt = now;
        const msg = isNetwork
            ? "Can't reach Atlas — check your connection."
            : "Something's off on our end. Please try again in a moment.";
        toast.error(msg, { duration: 4500 });
    } catch {
        /* toast is best-effort; never throw from an interceptor */
    }
}

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const url = original?.url || "";
        if (
            status !== 401 ||
            NO_REFRESH_PATHS.some((p) => url.includes(p))
        ) {
            surfaceGlobalError(error);
            return Promise.reject(error);
        }
        const attempt = original._retryCount || 0;
        if (attempt >= 2) {
            surfaceGlobalError(error);
            return Promise.reject(error);
        }
        original._retryCount = attempt + 1;
        try {
            await tryRefresh();
        } catch (refreshErr) {
            signalAuthExpired();
            return Promise.reject(error);
        }
        // First retry immediate; second after 150ms for slow cookie commits.
        await sleep(attempt === 0 ? 0 : 150);
        // Refresh the Authorization header for the retry (a new token was cached)
        const freshToken = tokenStore.getAccess();
        if (freshToken) {
            original.headers = { ...(original.headers || {}), Authorization: `Bearer ${freshToken}` };
        }
        return api(original);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
